import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Paperclip, X, Loader2 } from "lucide-react";
import { Field, SelectField, TextArea } from "@/components/site/FormControls";
import { adminApi, getFieldServices, createServiceRequest, uploadAttachment, apiError, money } from "@/lib/api";
import { FIELD_CATEGORIES, PROPERTY_TYPES, URGENCY } from "@/data/fieldServices";
import { OCCUPANCY } from "@/data/inspections";

const EMPTY = {
  full_name: "", company: "", email: "", phone: "", address: "", city: "", state: "PA", zip: "", property_type: "", occupancy: "",
  service_id: "", preferred_date: "", preferred_time: "", interior_access: false, contact_required: false, instructions: "",
  project_size: "", num_properties: 1, urgency: "Standard", additional_services: "", website: "",
};

const Group = ({ title, children }) => (
  <fieldset className="grid gap-5 border-t border-slate-200 pt-6 sm:grid-cols-2">
    <legend className="pr-3 font-mono-x text-xs uppercase tracking-widest text-gold">{title}</legend>
    {children}
  </fieldset>
);

const Toggle = ({ label, name, checked, onChange }) => (
  <label className="flex items-center gap-3 rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-700">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(name, e.target.checked)} data-testid={`req-${name}`} /> {label}
  </label>
);

const Done = ({ r }) => (
  <div data-testid="request-confirmation" className="mx-auto max-w-xl rounded-3xl border border-slate-200 bg-white p-10 text-center">
    <CheckCircle2 className="mx-auto h-14 w-14 text-gold" />
    <h2 className="mt-5 font-display text-3xl font-bold text-[#0B132B]">Request received</h2>
    <p className="mt-3 text-slate-600">Your request number is</p>
    <p data-testid="request-number" className="mt-2 font-mono-x text-2xl font-bold tracking-widest text-[#0B132B]">{r.request_number}</p>
    <p className="mt-5 text-sm text-slate-500">This is not a confirmed appointment. We'll review your request and email {r.email} with a quote or scheduled time.
      {r.estimate?.total != null && <> Estimated price: <b>{money(r.estimate.total)}</b>.</>}</p>
    <Link to="/client-portal" className="mt-6 inline-block text-sm font-semibold text-gold underline underline-offset-4">Track it in the client portal</Link>
  </div>
);

export const RequestForm = ({ category }) => {
  const [params] = useSearchParams();
  const [f, setF] = useState({ ...EMPTY, service_id: params.get("service") || "" });
  const [services, setServices] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const set = (e) => setF({ ...f, [e.target.name]: e.target.value });
  const setVal = (k, v) => setF({ ...f, [k]: v });

  useEffect(() => {
    getFieldServices().then(setServices).catch(() => setServices([]));
    adminApi.get("/auth/me").then(({ data: u }) => setF((p) => ({ ...p, full_name: p.full_name || u.name || "", email: p.email || u.email, phone: p.phone || u.phone || "", company: p.company || u.company || "" }))).catch(() => {});
  }, []);

  useEffect(() => {
    if (done) document.querySelector("[data-testid='request-confirmation']")?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [done]);

  const list = category ? services.filter((s) => s.category === category) : services;

  const onFiles = async (e) => {
    const picked = [...e.target.files].slice(0, 10 - files.length);
    e.target.value = "";
    setUploading(true);
    for (const file of picked) {
      try {
        const up = await uploadAttachment(file);
        setFiles((l) => [...l, up]);
      } catch (ex) {
        toast.error(`${file.name}: ${apiError(ex)}`);
      }
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.service_id) return toast.error("Please choose a service.");
    if (!/^\d{5}$/.test(f.zip)) return toast.error("Please enter a 5-digit ZIP code.");
    setSending(true);
    try {
      setDone(await createServiceRequest({ ...f, num_properties: Number(f.num_properties) || 1, attachment_ids: files.map((x) => x.id) }));
    } catch (ex) {
      toast.error(apiError(ex));
    } finally {
      setSending(false);
    }
  };

  if (done) return <Done r={done} />;

  return (
    <form onSubmit={submit} data-testid="request-form" className="space-y-8 rounded-3xl border border-slate-200 bg-white p-6 sm:p-10">
      <Group title="Service information">
        <div className="sm:col-span-2">
          <label className="mb-1.5 block text-sm font-medium text-slate-600">Service requested <span className="text-gold">*</span></label>
          <select name="service_id" value={f.service_id} onChange={set} required data-testid="req-service"
            className="w-full rounded-lg border border-slate-300 bg-[#FAF8F1] px-4 py-2.5 text-sm text-[#0B132B] focus:border-gold focus:outline-none">
            <option value="">Select a service…</option>
            {FIELD_CATEGORIES.filter((c) => !category || c.id === category).map((c) => (
              <optgroup key={c.id} label={c.name}>
                {list.filter((s) => s.category === c.id).map((s) => <option key={s.id} value={s.id}>{s.name}{s.base_price != null ? ` (from $${s.base_price})` : ""}</option>)}
              </optgroup>
            ))}
          </select>
        </div>
        <Field label="Preferred inspection date" name="preferred_date" type="date" value={f.preferred_date} onChange={set} half testid="req-date" />
        <SelectField label="Preferred time" name="preferred_time" value={f.preferred_time} onChange={set} options={["Morning", "Midday", "Afternoon", "Flexible"]} half testid="req-time" />
        <Toggle label="Interior access required?" name="interior_access" checked={f.interior_access} onChange={setVal} />
        <Toggle label="Contact required?" name="contact_required" checked={f.contact_required} onChange={setVal} />
        <TextArea label="Special instructions" name="instructions" value={f.instructions} onChange={set} testid="req-instructions" />
        <div className="sm:col-span-2">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-dashed border-slate-400 px-5 py-2.5 text-sm text-slate-600 hover:border-gold">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} Upload photos or documents
            <input type="file" multiple onChange={onFiles} className="hidden" data-testid="req-files" accept="image/*,.pdf,.doc,.docx,.xlsx,.csv,.txt" />
          </label>
          <div className="mt-3 flex flex-wrap gap-2">
            {files.map((x) => (
              <span key={x.id} className="inline-flex items-center gap-1 rounded-full bg-[#F5F3EC] px-3 py-1 text-xs">{x.original_filename}
                <button type="button" onClick={() => setFiles(files.filter((y) => y.id !== x.id))}><X className="h-3 w-3" /></button></span>
            ))}
          </div>
          <p className="mt-1 text-xs text-slate-400">Up to 10 files, 10 MB each.</p>
        </div>
      </Group>
      <Group title="Property information">
        <Field label="Property address" name="address" value={f.address} onChange={set} required testid="req-address" />
        <Field label="City" name="city" value={f.city} onChange={set} required half testid="req-city" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="State" name="state" value={f.state} onChange={set} required half testid="req-state" />
          <Field label="ZIP code" name="zip" value={f.zip} onChange={set} required half testid="req-zip" />
        </div>
        <SelectField label="Property type" name="property_type" value={f.property_type} onChange={set} options={PROPERTY_TYPES} required half testid="req-property-type" />
        <SelectField label="Occupancy status" name="occupancy" value={f.occupancy} onChange={set} options={OCCUPANCY} required half testid="req-occupancy" />
      </Group>
      <Group title="Project information">
        <Field label="Estimated project size" name="project_size" value={f.project_size} onChange={set} half placeholder="e.g. 2,400 sq ft / 3 units" testid="req-project-size" />
        <Field label="Number of properties" name="num_properties" type="number" value={f.num_properties} onChange={set} half testid="req-num-properties" />
        <SelectField label="Urgency" name="urgency" value={f.urgency} onChange={set} options={URGENCY} half testid="req-urgency" />
        <Field label="Additional services needed" name="additional_services" value={f.additional_services} onChange={set} half testid="req-additional" />
      </Group>
      <Group title="Customer information">
        <Field label="Full name" name="full_name" value={f.full_name} onChange={set} required half testid="req-full-name" />
        <Field label="Company name" name="company" value={f.company} onChange={set} half testid="req-company" />
        <Field label="Email" name="email" type="email" value={f.email} onChange={set} required half testid="req-email" />
        <Field label="Phone number" name="phone" type="tel" value={f.phone} onChange={set} required half testid="req-phone" />
      </Group>
      <input type="text" name="website" value={f.website} onChange={set} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      <button type="submit" disabled={sending || uploading} data-testid="req-submit"
        className="w-full rounded-full bg-gold py-4 text-base font-bold text-[#0B132B] transition-colors hover:bg-[#E5C158] disabled:opacity-60">
        {sending ? "Sending…" : "Request Inspection / Get a Quote"}
      </button>
      <p className="text-center text-xs text-slate-500">Submitting a request does not confirm an appointment. We'll follow up after review.</p>
    </form>
  );
};
